import React from 'react';
import { Toaster } from '@/components/ui/sonner';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from '@/lib/auth';
import { FeatureFlagsProvider } from '@/lib/featureFlags';
import { PlatformSettingsProvider } from '@/lib/platformSettings';
import '@/App.css';

import PublicLayout from '@/components/marketplace/PublicLayout';
import DashboardLayout from '@/components/marketplace/DashboardLayout';
import ScrollToTop from '@/components/marketplace/ScrollToTop';
import RedirectGate from '@/components/marketplace/RedirectGate';
import AnalyticsProvider from '@/components/marketplace/AnalyticsProvider';
import ConsentBanner from '@/components/marketplace/ConsentBanner';

import Home from '@/pages/Home';
import Browse from '@/pages/Browse';
import ListingDetail from '@/pages/ListingDetail';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import PostListing from '@/pages/PostListing';
import DashboardHome from '@/pages/DashboardHome';
import MyListings from '@/pages/MyListings';
import InquiriesInbox from '@/pages/InquiriesInbox';
import RevealLog from '@/pages/RevealLog';
import VerificationCenter from '@/pages/VerificationCenter';
import CompanyProfile from '@/pages/CompanyProfile';
import EmailPreferences from '@/pages/EmailPreferences';
import Billing from '@/pages/Billing';
import ModeratorPanel from '@/pages/ModeratorPanel';
import AdminPanel from '@/pages/AdminPanel';
import AdminBlog from '@/pages/AdminBlog';
import AdminBlogEditor from '@/pages/AdminBlogEditor';
import AdminUserDetail from '@/pages/AdminUserDetail';
import AdminPayments from '@/pages/AdminPayments';
import AdminCMS from '@/pages/AdminCMS';
import AdminRedirects from '@/pages/AdminRedirects';
import AdminEmails from '@/pages/AdminEmails';
import AdminCampaigns from '@/pages/AdminCampaigns';
import AdminPromotions from '@/pages/AdminPromotions';
import AdminReferrals from '@/pages/AdminReferrals';
import AdminFeedback from '@/pages/AdminFeedback';
import InviteAndEarn from '@/pages/InviteAndEarn';
import ReferralRedirect from '@/pages/ReferralRedirect';
import CategoryPage from '@/pages/CategoryPage';
import CategoriesDirectory from '@/pages/CategoriesDirectory';
import LocationsDirectory from '@/pages/LocationsDirectory';
import LocationPage from '@/pages/LocationPage';
import SellerProfile from '@/pages/SellerProfile';
import { MyFavorites, MyFollowing } from '@/pages/MyFavorites';
import MyReviews from '@/pages/MyReviews';
import StaticPage, { StaticPageBySlug } from '@/pages/StaticPage';
import Pricing from '@/pages/Pricing';
import Blog from '@/pages/Blog';
import BlogPost from '@/pages/BlogPost';
import Features from '@/pages/Features';
import MyInvoices from '@/pages/MyInvoices';
import MascotPreview from '@/pages/MascotPreview';
import ComparePage from '@/pages/ComparePage';
import Unsubscribe from '@/pages/Unsubscribe';
import MyComparisons from '@/pages/MyComparisons';
import NotFound from '@/pages/NotFound';
import OnboardingTour from '@/components/marketplace/OnboardingTour';

// Staff panels are gated by PERMISSIONS from /api/me/permissions (the same
// catalog the backend enforces), never by role strings — so a custom RBAC role
// holding `admin.access` / `moderator.access` can open the matching panel, and
// a custom role without them cannot.
const RequireAuth = ({ children, permissions }) => {
  const { user, loading, permissions: granted, hasAnyPermission } = useAuth();
  if (loading) return <div className='min-h-screen flex items-center justify-center text-muted-foreground'>Loading…</div>;
  if (!user) return <Navigate to='/login' replace />;
  if (permissions) {
    // Wait for the permission set to resolve before deciding.
    if (granted === null) return <div className='min-h-screen flex items-center justify-center text-muted-foreground'>Loading…</div>;
    if (!hasAnyPermission(permissions)) return <Navigate to='/dashboard' replace />;
  }
  return children;
};

function App() {
  return (
    <div className='App'>
      <HelmetProvider>
      <AuthProvider>
        <FeatureFlagsProvider>
        <PlatformSettingsProvider>
        <BrowserRouter>
          <ScrollToTop />
          <RedirectGate />
          <AnalyticsProvider />
          <ConsentBanner />
          <OnboardingTour />
          <Routes>
            {/* Public */}
            <Route element={<PublicLayout />}>
              <Route path='/' element={<Home />} />
              <Route path='/sell' element={<Browse listingType='sell' />} />
              <Route path='/wanted' element={<Browse listingType='wanted' />} />
              <Route path='/rent' element={<Browse listingType='rent' />} />
              <Route path='/services' element={<Browse listingType='service' />} />
              <Route path='/listing/:id' element={<ListingDetail />} />
              {/* Canonical SEO listing URLs: /<type>/<category>/<subcategory>/<slug>.
                  Explicit per-type prefixes avoid clashing with top-level routes
                  like /pricing or /blog. The last segment resolves the listing. */}
              {['sell', 'wanted', 'rent', 'services'].map((t) => (
                <React.Fragment key={t}>
                  <Route path={`/${t}/:catSlug/:subSlug/:slug`} element={<ListingDetail />} />
                  <Route path={`/${t}/:catSlug/:slug`} element={<ListingDetail />} />
                </React.Fragment>
              ))}
              <Route path='/category/:listingType/*' element={<CategoryPage />} />
              <Route path='/categories' element={<CategoriesDirectory />} />
              <Route path='/locations' element={<LocationsDirectory />} />
              <Route path='/locations/:slug' element={<LocationPage />} />
              <Route path='/pricing' element={<Pricing />} />
              {/* Compare is a private working view — noindex, never a content page. */}
              <Route path='/compare' element={<ComparePage />} />
              <Route path='/compare/s/:token' element={<ComparePage sharedMode />} />
              <Route path='/features' element={<Features />} />
              {/* One-click unsubscribe from announcement email — no login needed. */}
              <Route path='/unsubscribe' element={<Unsubscribe />} />
              <Route path='/_mascots' element={<MascotPreview />} />
              <Route path='/blog' element={<Blog />} />
              <Route path='/blog/category/:slug' element={<Blog />} />
              <Route path='/blog/:slug' element={<BlogPost />} />
              <Route path='/about' element={<StaticPage page='about' />} />
              <Route path='/contact' element={<StaticPage page='contact' />} />
              <Route path='/faq' element={<StaticPage page='faq' />} />
              <Route path='/privacy' element={<StaticPage page='privacy' />} />
              <Route path='/terms' element={<StaticPage page='terms' />} />
              <Route path='/policies' element={<StaticPage page='policies' />} />
              <Route path='/refund' element={<StaticPage page='refund' />} />
              <Route path='/cancellation' element={<StaticPage page='cancellation' />} />
              <Route path='/shipping' element={<StaticPage page='shipping' />} />
              {/* Sitemap-friendly catch-all — /policy/<slug> resolves to any active static page. */}
              <Route path='/policy/:slug' element={<StaticPageBySlug />} />
              <Route path='/login' element={<Login />} />
              <Route path='/register' element={<Register />} />
              <Route path='/forgot-password' element={<ForgotPassword />} />
              <Route path='/reset-password' element={<ResetPassword />} />
              <Route path='/seller/:id' element={<SellerProfile />} />
              <Route path='/r/:code' element={<ReferralRedirect />} />
              {/* Unknown paths: NotFound first consults the admin redirect map;
                  if no redirect exists it renders a real 404 (noindex). Keeps
                  the public header/footer. Never bounces to home. */}
              <Route path='*' element={<NotFound />} />
            </Route>

            {/* Authenticated: post listing */}
            <Route
              path='/post'
              element={
                <RequireAuth>
                  <PublicLayout>
                    <PostListing />
                  </PublicLayout>
                </RequireAuth>
              }
            />
            <Route
              path='/post/:id/edit'
              element={
                <RequireAuth>
                  <PublicLayout>
                    <PostListing editMode />
                  </PublicLayout>
                </RequireAuth>
              }
            />

            {/* Dashboards */}
            <Route
              path='/dashboard'
              element={
                <RequireAuth>
                  <DashboardLayout />
                </RequireAuth>
              }
            >
              <Route index element={<DashboardHome />} />
              <Route path='listings' element={<MyListings />} />
              <Route path='inquiries' element={<InquiriesInbox />} />
              <Route path='reveal-log' element={<RevealLog />} />
              <Route path='verification' element={<VerificationCenter />} />
              <Route path='profile' element={<CompanyProfile />} />
              <Route path='notifications' element={<EmailPreferences />} />
              <Route path='favorites' element={<MyFavorites />} />
              <Route path='comparisons' element={<MyComparisons />} />
              <Route path='following' element={<MyFollowing />} />
              <Route path='reviews' element={<MyReviews />} />
              <Route path='billing' element={<Billing />} />
              <Route path='invoices' element={<MyInvoices />} />
              <Route path='invite' element={<InviteAndEarn />} />
            </Route>

            <Route
              path='/moderator/*'
              element={
                <RequireAuth permissions={['moderator.access']}>
                  <DashboardLayout moderator />
                </RequireAuth>
              }
            >
              <Route index element={<ModeratorPanel />} />
            </Route>

            <Route
              path='/admin/*'
              element={
                <RequireAuth permissions={['admin.access']}>
                  <DashboardLayout admin />
                </RequireAuth>
              }
            >
              <Route index element={<AdminPanel />} />
              <Route path='users/:userId' element={<AdminUserDetail />} />
              <Route path='cms' element={<AdminCMS />} />
              <Route path='redirects' element={<AdminRedirects />} />
              <Route path='blog' element={<AdminBlog />} />
              <Route path='blog/new' element={<AdminBlogEditor />} />
              <Route path='blog/:id/edit' element={<AdminBlogEditor editMode />} />
              <Route path='payments' element={<AdminPayments />} />
              <Route path='emails' element={<AdminEmails />} />
              <Route path='campaigns' element={<AdminCampaigns />} />
              <Route path='promotions' element={<AdminPromotions />} />
              <Route path='referrals' element={<AdminReferrals />} />
              <Route path='feedback' element={<AdminFeedback />} />
            </Route>

          </Routes>
        </BrowserRouter>
        <Toaster position='top-right' richColors />
        </PlatformSettingsProvider>
        </FeatureFlagsProvider>
      </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
